import type { IdentityVerificationSummary } from "@repo/shared/types";
import { BadgeCheck } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./verified-id-badge.i18n";

/**
 * Match the icon to whatever it sits beside.
 *
 * The badge shares a line with different neighbours on different surfaces — the
 * 24px favourite star in a page header, 16px text in a table row — and an icon
 * that ignores that reads as a mistake rather than a system.
 */
export type VerifiedIdBadgeSize = "sm" | "lg";

const SIZE_CLASS: Record<VerifiedIdBadgeSize, string> = {
  sm: "h-4 w-4",
  lg: "h-6 w-6",
};

type VerifiedIdBadgeProps = {
  summary: IdentityVerificationSummary | null | undefined;
  /** `lg` beside a page title, `sm` (default) inline in tables and lists. */
  size?: VerifiedIdBadgeSize;
  onOpen?: () => void;
};

/**
 * QL-e — the Verified ID icon: one small green check, immediately after the
 * name it belongs to.
 *
 * Deliberately renders NOTHING when there is no valid verification. There is no
 * grey or crossed-out "unverified" state: absence of the icon is the absence of
 * the claim, and an explicit negative badge would imply we had checked and
 * found something wanting, which is not what "we hold no verification" means.
 *
 * The icon itself carries no information beyond "verified" — no name, no date,
 * no source. All of that lives in the modal behind it, which is why the same
 * component renders identically on every surface with no per-surface variants
 * to keep in sync.
 */
export function VerifiedIdBadge({ summary, size = "sm", onOpen }: VerifiedIdBadgeProps) {
  const t = useT(T);
  if (!summary?.verified) return null;

  const label = summary.isTestMode ? `${t.verifiedId} (${t.testMode})` : t.verifiedId;

  // Carries its OWN provider: the app has no TooltipProvider at the root (only
  // the editor mounts one), and Radix throws without an ancestor provider. This
  // badge drops into seven unrelated surfaces, so it has to work wherever it
  // lands rather than depending on what happens to wrap it.
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={(event) => {
              // Surfaces put this inside clickable table rows; opening the modal
              // must not also navigate to the client.
              event.stopPropagation();
              onOpen?.();
            }}
            aria-label={label}
            className="inline-flex shrink-0 items-center text-success hover:opacity-80 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded-sm"
          >
            <BadgeCheck
              className={`${SIZE_CLASS[size]} ${summary.isTestMode ? "opacity-60" : ""}`}
              aria-hidden="true"
            />
          </button>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
